import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import ProtectedCaseStudy from '../component/ProtectedCaseStudy';

const TysonCaseStudy = () => {
  return (
    <ProtectedCaseStudy pageName="tyson" correctPassword="DJdesign1!">
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-dj-dark py-4 px-4 md:px-20">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-secondary transition-colors">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Home</span>
        </Link>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-dj-dark to-dj-dark/95 text-white py-16 md:py-24 px-4 md:px-20">
        <div className="max-w-4xl mx-auto">
          <p className="text-primary text-xs font-semibold uppercase tracking-widest mb-4">Portfolio Case Study</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">Unifying a fragmented digital ecosystem across 11 national consumer brands</h1>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">How I led platform strategy, content governance, design system buildout, data integration, and enterprise migration at Tyson Foods — owning every layer from content audit to launch.</p>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 border-t border-gray-600 pt-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Company</p>
              <p className="text-sm font-semibold">Tyson Foods, Inc.</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Role</p>
              <p className="text-sm font-semibold">Digital Experience & MarTech Lead</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Timeline</p>
              <p className="text-sm font-semibold">April 2025 – Present</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Scope</p>
              <p className="text-sm font-semibold">Strategy + Execution</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Brands</p>
              <p className="text-sm font-semibold">11 National</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="py-16 md:py-24 px-4 md:px-20">
        <div className="max-w-4xl mx-auto space-y-20">
          
          {/* Section 01 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">01 — The System</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Eleven brands. Four platforms. No shared infrastructure, content model, or governance.</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">Tyson Foods operates a portfolio of national consumer brands — Tyson, Jimmy Dean, Hillshire Farm, Wright Brand, Ball Park, State Fair, Aidells, Nature Raised Farms, Gallo Salame, Hillshire Snacking, and TortillaLand. Each had its own website, its own CMS, its own asset library, its own content, and its own way of working. The result was a fragmented digital ecosystem that was expensive to maintain, impossible to govern consistently, and unable to scale modern marketing activation.</p>
            <p className="text-gray-700 mb-8 leading-relaxed">Before any recommendations could be made, the full system needed to be understood at every layer — technology, content, data, people, process, and brand standards.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: '🏗️', title: 'Fragmented platforms', desc: 'Brands ran on Drupal, WordPress, legacy systems, and custom-built sites — each requiring separate maintenance and vendor relationships.' },
                { icon: '📄', title: 'Unaudited content', desc: 'No brand had a current content inventory. Legacy content was untouched, unreviewed, and in many cases legally uncleared for migration.' },
                { icon: '🎨', title: 'No design system', desc: 'No shared component library existed. Developers had no AEM build specifications — brand digital expression was inconsistent across every site.' },
                { icon: '📦', title: 'Disconnected product data', desc: 'Product information in Salsify and recipe content weren\'t connected to the new platform — data pipelines had to be built and validated from scratch.' },
                { icon: '📁', title: 'No asset governance', desc: 'Digital assets scattered across drives, brand folders, and agency servers — no taxonomy, no metadata standards, no single source of truth.' },
                { icon: '🔀', title: 'Disconnected stakeholders', desc: 'Marketing, IT, legal, agencies, and brand teams operated with different priorities and no shared operating model.' },
              ].map((item, idx) => (
                <div key={idx} className="bg-dj-light p-6 rounded-lg border-t-4 border-secondary">
                  <p className="text-2xl mb-2">{item.icon}</p>
                  <h4 className="font-bold text-dj-dark mb-2">{item.title}</h4>
                  <p className="text-gray-700 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 02 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">02 — Service Blueprint</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Mapping the full system before designing any solution</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">Before recommending any platform or tooling changes, I mapped the complete digital content system — frontstage (what brand teams and consumers experienced), backstage (what IT, legal, and agencies managed), and the structural points where breakdowns were occurring.</p>
            
            <div className="bg-dj-light p-8 rounded-lg overflow-x-auto mb-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-dj-dark mb-6 text-center">Digital Content System — Service Blueprint (Simplified)</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-dj-dark text-white">
                    <th className="p-3 text-left font-semibold">Layer</th>
                    <th className="p-3 text-left font-semibold">Content Audit</th>
                    <th className="p-3 text-left font-semibold">Asset Governance</th>
                    <th className="p-3 text-left font-semibold">Design System</th>
                    <th className="p-3 text-left font-semibold">Data Integration</th>
                    <th className="p-3 text-left font-semibold">Publishing</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-300">
                    <td className="p-3 font-semibold bg-white">Frontstage</td>
                    <td className="p-3 bg-red-100 text-red-700">No content inventory</td>
                    <td className="p-3 bg-red-100 text-red-700">Assets scattered</td>
                    <td className="p-3 bg-red-100 text-red-700">No visual standards</td>
                    <td className="p-3 bg-red-100 text-red-700">Product data disconnected</td>
                    <td className="p-3 bg-red-100 text-red-700">Publishing manual</td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="p-3 font-semibold bg-white">Backstage</td>
                    <td className="p-3 bg-red-100 text-red-700">Legal late-stage</td>
                    <td className="p-3 bg-white">Agencies hold assets</td>
                    <td className="p-3 bg-red-100 text-red-700">No component specs</td>
                    <td className="p-3 bg-red-100 text-red-700">Salsify not mapped</td>
                    <td className="p-3 bg-red-100 text-red-700">4 separate CMS</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold bg-primary/20">Designed State</td>
                    <td className="p-3 bg-green-100 text-green-700">Content audited & legal-cleared</td>
                    <td className="p-3 bg-green-100 text-green-700">DAM with taxonomy</td>
                    <td className="p-3 bg-green-100 text-green-700">Brand-aligned system</td>
                    <td className="p-3 bg-green-100 text-green-700">Salsify flowing to AEM</td>
                    <td className="p-3 bg-green-100 text-green-700">AEM unified CMS</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-primary/10 border-l-4 border-primary p-6 rounded">
              <p className="text-dj-dark font-semibold">The blueprint made visible what no one had named: this wasn't a platform migration problem — it was a content, data, governance, and organizational problem that a platform change alone would not solve. Every layer needed to be designed, not just the technology.</p>
            </div>
          </section>

          {/* Section 03 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">03 — Content Audit & Governance</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">You can't migrate content you haven't audited</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">Across all 11 brands, I led the process of scraping legacy site content, working with each brand and business unit to make keep/remove/edit decisions for every content item, and routing everything through Tyson's legal department before migration began. Migrating unreviewed legacy content would have embedded legal risk, outdated information, and brand-inconsistent copy directly into the new platform from day one.</p>
          </section>

          {/* Section 04 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">04 — Proof of Concept</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Hillshire Farm: building the methodology before scaling it</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">Rather than attempting to migrate all 11 brands simultaneously, I partnered with a consulting agency to establish Hillshire Farm as the proof-of-concept — running a full discovery-to-launch process that would become the repeatable methodology for every subsequent brand migration.</p>
            
            <div className="bg-primary/10 border-l-4 border-primary p-6 rounded mb-8">
              <p className="text-dj-dark font-semibold">The goal wasn't just to launch Hillshire Farm. It was to build and validate a migration methodology — including content audit, design system buildout, data integration, and UAT — that could be executed faster and more efficiently with every brand that followed.</p>
            </div>

            <div className="space-y-4">
              {[
                { step: '01', phase: 'Discovery', owner: 'Me + Agency', color: 'bg-purple-100', desc: 'Mapped the Hillshire Farm digital ecosystem — existing site, brand standards, content inventory, stakeholder requirements, and technical constraints.' },
                { step: '02', phase: 'Content Audit & Legal Review', owner: 'Me — Content Lead', color: 'bg-blue-100', desc: 'Scraped all existing content, worked with the brand and BU to make keep/remove/edit decisions, then routed through Tyson\'s legal department.' },
                { step: '03', phase: 'Research & Brand Alignment', owner: 'Me + Agency', color: 'bg-purple-100', desc: 'Reviewed brand guides and worked directly with brand team to understand how visual identity should translate into AEM.' },
                { step: '04', phase: 'Global Design System', owner: 'Me + Agency', color: 'bg-purple-100', desc: 'Built single global design system mapped to shared AEM component library — foundation for all 11 brands.' },
                { step: '05', phase: 'Design System Review', owner: 'Me + UX Team', color: 'bg-blue-100', desc: 'Reviewed and validated design system with internal UX team before development began.' },
                { step: '06', phase: 'Data Integration', owner: 'Me — Data Validation', color: 'bg-blue-100', desc: 'Validated Salsify integration and ensured recipe content was properly structured in AEM Content Fragments.' },
                { step: '07', phase: 'Development', owner: 'Agency + Devs', color: 'bg-yellow-100', desc: 'Agency developers and internal dev teams built AEM infrastructure in parallel dev environments.' },
                { step: '08', phase: 'UAT & QA', owner: 'Me — Business Validation', color: 'bg-blue-100', desc: 'Served as business-side validator, reviewed developer output and tested all components.' },
                { step: '09', phase: 'Launch', owner: 'Launch + Methodology', color: 'bg-green-100', desc: 'Hillshire Farm launched. Every step was formalized as the repeatable migration methodology.' },
              ].map((item) => (
                <div key={item.step} className={`flex gap-4 ${item.color} p-4 rounded-lg`}>
                  <div className="font-bold text-lg text-dj-dark min-w-12">{item.step}</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-dj-dark">{item.phase}</h4>
                    <p className="text-xs text-gray-600 mb-2">{item.owner}</p>
                    <p className="text-sm text-gray-700">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 05 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">05 — Design System & Brand Alignment</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Building the component layer that made consistency possible</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">A platform migration without a design system produces a new CMS with the same inconsistency problem. Working with the consulting partner, I helped build a single global design system mapped directly to the shared AEM component library — the structural foundation all 11 brands would use.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {[
                { icon: '🔍', title: 'Brand guide archaeology', desc: 'Tracked down brand guides from individual business units across all 11 brands.' },
                { icon: '🎨', title: 'Color & typography validation', desc: 'Worked with brand teams to confirm web-accurate colors, type families, and spacing.' },
                { icon: '✅', title: 'Design system review', desc: 'Reviewed design system with Tyson\'s internal UX team before development.' },
                { icon: '🧱', title: 'Global system + brand theming', desc: 'Built one global design system tied to shared AEM components — re-skinned each brand.' },
              ].map((item, idx) => (
                <div key={idx} className="bg-dj-light p-6 rounded-lg">
                  <p className="text-2xl mb-2">{item.icon}</p>
                  <h4 className="font-bold text-dj-dark mb-2">{item.title}</h4>
                  <p className="text-gray-700 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 06 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">06 — Data Integration</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Connecting product data and recipe content to the platform</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">Two critical data layers had to be built, validated, and maintained for the new platform to function correctly at launch. I owned the business-side validation of both.</p>

            <h3 className="text-xl font-bold text-dj-dark mb-4">Salsify → AEM: Product data pipeline</h3>
            <p className="text-gray-700 mb-8 leading-relaxed">Salsify serves as Tyson's product information management (PIM) system. I validated that the integration was working correctly for each brand — verifying product data rendered accurately, identifying mismatches, and working with technical teams to resolve discrepancies before launch.</p>

            <div className="flex flex-col md:flex-row items-center gap-4 mb-8 bg-primary/10 p-6 rounded-lg">
              <div className="text-center flex-1">
                <p className="font-semibold text-dj-dark">Salsify (PIM)</p>
                <p className="text-sm text-gray-600">Product data</p>
              </div>
              <div className="text-2xl text-primary">→</div>
              <div className="text-center flex-1">
                <p className="font-semibold text-dj-dark">Validation</p>
                <p className="text-sm text-gray-600">Derek's review</p>
              </div>
              <div className="text-2xl text-primary">→</div>
              <div className="text-center flex-1">
                <p className="font-semibold text-dj-dark">AEM Pages</p>
                <p className="text-sm text-gray-600">Consumer-facing</p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-dj-dark mb-4 mt-8">AEM Content Fragments: Recipe content</h3>
            <p className="text-gray-700 leading-relaxed">AEM Content Fragments function as the platform's structured content database. I was responsible for ensuring recipe data was accurately added — working with content authors and technical teams to define the content model and validate accuracy before launch.</p>
          </section>

          {/* Section 07 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">07 — Migration at Scale</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Getting faster with every brand</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">Once the Hillshire Farm methodology was proven, it became the template for every subsequent brand migration. Each iteration refined the process and compressed time from kickoff to launch.</p>

            <div className="space-y-3">
              {[
                { brand: 'Hillshire Farm', effort: 100, note: 'POC — full build' },
                { brand: 'Jimmy Dean', effort: 82, note: '↑ Faster' },
                { brand: 'Tyson', effort: 70, note: '↑ Faster' },
                { brand: 'Wright Brand', effort: 58, note: '↑ Faster' },
                { brand: 'Ball Park', effort: 50, note: '↑ Faster' },
                { brand: 'State Fair', effort: 44, note: '↑ Faster' },
                { brand: 'Aidells', effort: 38, note: '↑ Faster' },
                { brand: 'Nature Raised Farms', effort: 32, note: '↑ Faster' },
                { brand: 'Gallo Salame', effort: 26, note: '↑ Faster' },
                { brand: 'Hillshire Snacking', effort: 22, note: '↑ Faster' },
                { brand: 'TortillaLand', effort: 18, note: '↑ Fastest' },
              ].map((item) => (
                <div key={item.brand} className="flex items-center gap-4">
                  <span className="font-semibold text-dj-dark min-w-40">{item.brand}</span>
                  <div className="flex-1 bg-gray-200 h-2 rounded-full overflow-hidden">
                    <div className="bg-primary h-full" style={{ width: `${item.effort}%` }}></div>
                  </div>
                  <span className="text-sm text-gray-600 min-w-20 text-right">{item.note}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 09 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">09 — Outcomes</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-8">What changed in the system</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[
                { stat: '11', label: 'national brands on unified platform' },
                { stat: '4→1', label: 'CMS platforms consolidated' },
                { stat: '90%+', label: 'asset coverage under new DAM' },
                { stat: '↑↑', label: 'migration velocity improved' },
              ].map((item, idx) => (
                <div key={idx} className="bg-gradient-to-br from-primary/20 to-secondary/20 p-6 rounded-lg text-center">
                  <p className="text-3xl font-bold text-primary mb-2">{item.stat}</p>
                  <p className="text-sm text-gray-700">{item.label}</p>
                </div>
              ))}
            </div>

            <ul className="space-y-4">
              {[
                'All 11 brand content libraries audited, categorized, and legally cleared before migration',
                'Hillshire Farm launched as first fully governed brand on AEM — validating end-to-end methodology',
                'Global design system built and mapped to shared AEM components',
                'Salsify product data flowing accurately into AEM across all brands',
                'Recipe content structured in AEM Content Fragments across all brands',
                'DAM governance framework in place with taxonomy and metadata standards',
                'DCX Workflow Guide adopted as shared operating model',
                'All brand sites optimized for SEO and AEO at launch',
              ].map((outcome, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="text-primary font-bold text-lg mt-1">→</span>
                  <p className="text-gray-700">{outcome}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 10 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">10 — Reflection</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">What this project taught me about systems design</h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded mb-8">
              <h3 className="font-bold text-dj-dark mb-3">The scope was wider than anyone initially defined</h3>
              <p className="text-gray-700">When this engagement began, the problem was framed as a platform migration. In practice, it was a content governance problem, a data integration problem, an organizational alignment problem, and a brand standards problem — all of which had to be solved before the platform migration could succeed.</p>
            </div>

            <h3 className="text-xl font-bold text-dj-dark mb-3">What I would do differently</h3>
            <p className="text-gray-700 mb-6">I would push for earlier involvement of brand teams in both the content audit and design system process. Brand guides are often aspirational documents that don't translate directly to digital — the negotiation between what's in the guide and what works on the web is faster when brand stakeholders are co-designing.</p>

            <h3 className="text-xl font-bold text-dj-dark mb-3">What I'd carry into future systems work</h3>
            <p className="text-gray-700">Audit before migrate. Govern before platform. Validate data before launch. Prove the methodology before scaling it. These sequencing principles — not specific tools or technologies — are what made this work repeatable across 11 brands in a compressed timeframe.</p>
          </section>

        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-dj-dark text-white text-center py-12 px-4">
        <p className="mb-4"><strong>Derek Johnston</strong> — Digital Product Strategist & UX Leader</p>
        <p className="text-sm text-gray-400">dj@derekjohnston.com · (312) 608-4894</p>
      </div>
    </div>
    </ProtectedCaseStudy>
  );
};

export default TysonCaseStudy;
